import { Dropdown, EHeadingStyleVariant, Heading, Text, TextInput } from '@outdoorsyco/bonfire';
import { useEffect, useState } from 'react';
import { Control, Controller, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useCountries, useCountriesRegion } from '@/services/static';
import { IFullUserInfo } from '@/services/types/user/user';

import { EPersonalInfoFields, TPersonalInfoValues } from '../ProfileEdit';

export interface ICountriesRegion {
  code: string;
  name: string;
}

type TAddressProps = {
  userData: IFullUserInfo;
  control: Control<TPersonalInfoValues>;
  errors: FieldErrors<TPersonalInfoValues>;
  setValue: UseFormSetValue<TPersonalInfoValues>;
};

const validStates = ['US', 'CA', 'AU'];

const orderedCountries = (countries: ICountriesRegion[]) => {
  const prioritizeCountries = ['US', 'CA', 'GB', 'AU', 'NZ'];

  // Create two separate arrays: one for the prioritized countries and one for the rest
  const prioritizedCountries: ICountriesRegion[] = [];
  const otherCountries: ICountriesRegion[] = [];

  prioritizeCountries.forEach(countryCode => {
    const country = countries.find(c => c.code === countryCode);
    if (country) {
      prioritizedCountries.push(country);
    }
  });

  // Loop through the countries array and add non-prioritized countries to otherCountries
  countries.forEach(country => {
    if (!prioritizeCountries.includes(country.code)) {
      otherCountries.push(country);
    }
  });

  // Combine the prioritized countries and the rest of the countries
  const sortedCountries = [...prioritizedCountries, ...otherCountries];

  return sortedCountries;
};

export const Address = ({ userData, control, errors, setValue }: TAddressProps) => {
  const intl = useIntl();
  const [countries, setCountries] = useState<ICountriesRegion[]>([]);
  const [state, setState] = useState<string>(userData.profile.address.country || 'US');

  const { data: countriesResponse } = useCountries();
  const { data: countiesRegionResponse } = useCountriesRegion(state);

  const regions = countiesRegionResponse || [];

  useEffect(() => {
    if (countriesResponse) {
      const result = orderedCountries(countriesResponse);
      setCountries(result);
    }
  }, [countriesResponse]);

  const getValue = (value: string) => {
    const stateValue = regions.find(a => a.code === value || a.name === value);
    return stateValue ? stateValue.code : '';
  };

  const changePickupCountry = (country: string) => {
    setState(country.toUpperCase());

    setValue('address1', '');
    setValue('address2', '');
    setValue('city', '');
    setValue('state', '');
    setValue('zip', '');
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Heading variant={EHeadingStyleVariant.H6} className="mb-2">
          <FormattedMessage defaultMessage="Address" id="e6Ph5+" />
        </Heading>
        <Text className="mb-2 text-secondary-base">
          <FormattedMessage
            defaultMessage="Make sure to use the permanent address where you receive mail."
            id="kDQpye"
          />
        </Text>
      </div>
      <div className="flex flex-col gap-3">
        <Controller
          name={EPersonalInfoFields.country}
          control={control}
          rules={{
            required: intl.formatMessage({
              defaultMessage: 'Country is a required field.',
              id: 'r1M4kM',
            }),
          }}
          render={({ field: { value, onChange } }) => {
            return (
              <Dropdown
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Country',
                  id: 'vONi+O',
                })}
                inputValue={value}
                label={intl.formatMessage({
                  defaultMessage: 'Country',
                  id: 'vONi+O',
                })}
                onChange={e => {
                  changePickupCountry(e);
                  onChange(e);
                }}>
                {countries.map(a => (
                  <Dropdown.Option
                    key={a.code}
                    content={{
                      label: a.name,
                    }}
                    value={a.code}
                  />
                ))}
              </Dropdown>
            );
          }}
        />
        <Controller
          name={EPersonalInfoFields.address1}
          control={control}
          rules={{
            required: intl.formatMessage({
              defaultMessage: 'Address is a required field.',
              id: 'TwQ8I7',
            }),
          }}
          render={({ field: { value, onChange } }) => {
            return (
              <TextInput
                controlled
                label={intl.formatMessage({
                  defaultMessage: 'Address',
                  id: 'e6Ph5+',
                })}
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Add a valid address',
                  id: 'v71dcU',
                })}
                inputValue={value}
                onChange={onChange}
                error={!!errors.address1}
                helpText={errors.address1?.message}
              />
            );
          }}
        />
        <Controller
          name={EPersonalInfoFields.address2}
          control={control}
          render={({ field: { value, onChange } }) => {
            return (
              <TextInput
                controlled
                label={intl.formatMessage({
                  defaultMessage: 'Apt., suite, etc. (optional)',
                  id: 'M5Zyzm',
                })}
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Add a second address (optional)',
                  id: 'j4JZZn',
                })}
                inputValue={value}
                onChange={onChange}
              />
            );
          }}
        />
        <Controller
          name={EPersonalInfoFields.city}
          control={control}
          rules={{
            required: intl.formatMessage({
              defaultMessage: 'City is a required field.',
              id: 'xy3LTW',
            }),
          }}
          render={({ field: { value, onChange } }) => {
            return (
              <TextInput
                controlled
                label={intl.formatMessage({ defaultMessage: 'City', id: 'TE4fIS' })}
                ariaLabel={intl.formatMessage({
                  defaultMessage: 'Add a city',
                  id: 'OVCL2N',
                })}
                inputValue={value}
                onChange={onChange}
                error={!!errors.city}
                helpText={errors.city?.message}
              />
            );
          }}
        />
        <div
          className={`grid grid-cols-1 gap-3 ${
            regions.length !== 0 && validStates.includes(state) && 'md:grid-cols-2'
          } md:gap-2`}>
          {regions.length !== 0 && validStates.includes(state) && (
            <div>
              <Controller
                name={EPersonalInfoFields.state}
                control={control}
                rules={{
                  required: intl.formatMessage({
                    defaultMessage: 'State is a required field.',
                    id: 'NvBH40',
                  }),
                }}
                render={({ field: { value, onChange } }) => {
                  return (
                    <Dropdown
                      label={intl.formatMessage({
                        defaultMessage: 'State',
                        id: 'ku+mDU',
                      })}
                      ariaLabel={intl.formatMessage({
                        defaultMessage: 'Add a state',
                        id: 'HUr64B',
                      })}
                      inputValue={getValue(value) || ''}
                      onChange={onChange}
                      error={!!errors.state}
                      helperText={errors.state?.message}>
                      {regions.map(a => (
                        <Dropdown.Option
                          key={a.name}
                          content={{
                            label: `${a.code} - ${a.name}`,
                          }}
                          value={a.code}
                        />
                      ))}
                    </Dropdown>
                  );
                }}
              />
            </div>
          )}
          <div>
            <Controller
              name={EPersonalInfoFields.zip}
              control={control}
              rules={{
                required: intl.formatMessage({
                  defaultMessage: 'Zip is a required field.',
                  id: 'yzg8PG',
                }),
              }}
              render={({ field: { value, onChange } }) => {
                return (
                  <TextInput
                    controlled
                    label={intl.formatMessage({ defaultMessage: 'ZIP', id: 's42plv' })}
                    ariaLabel={intl.formatMessage({
                      defaultMessage: 'Add a zip code',
                      id: 'Jfz6Eq',
                    })}
                    inputValue={value}
                    onChange={onChange}
                    error={!!errors.zip}
                    helpText={errors.zip?.message}
                  />
                );
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
